<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
  format,
  getISOWeek,
  getISOWeeksInYear,
  getYear,
  setISOWeek,
  setYear,
  isAfter,
  parseISO,
} from 'date-fns';

const props = defineProps({
  initialFrom: {
    type: Number,
    default: null,
  },
  initialTo: {
    type: Number,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange', 'resetFilter']);
const { t } = useI18n();

// Mode: 'weekly' or 'custom'
const filterMode = ref('weekly');

const currentYear = getYear(new Date());
const selectedYear = ref(currentYear);

// Dynamic list of selectable years (current, previous years, and upcoming year)
const yearOptions = computed(() => [
  currentYear + 1,
  currentYear,
  currentYear - 1,
  currentYear - 2,
  currentYear - 3,
]);

const availableWeeks = computed(() => {
  const weeks = [];
  const now = new Date();
  const currentIsoWeek = getISOWeek(now);
  const totalWeeks = getISOWeeksInYear(new Date(selectedYear.value, 0, 4));
  
  // Build weeks for selected year (handles 52 or 53 ISO weeks automatically)
  for (let w = totalWeeks; w >= 1; w--) {
    let dummy = setYear(new Date(selectedYear.value, 0, 4), selectedYear.value);
    dummy = setISOWeek(dummy, w);
    const start = startOfWeek(dummy, { weekStartsOn: 1 });
    const end = endOfWeek(dummy, { weekStartsOn: 1 });
    
    weeks.push({
      weekNumber: w,
      year: selectedYear.value,
      label: `Week ${w}, ${selectedYear.value} (${format(start, 'dd-MMM-yyyy')} to ${format(end, 'dd-MMM-yyyy')})`,
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
      startTimestamp: Math.floor(start.getTime() / 1000),
      endTimestamp: Math.floor(end.getTime() / 1000),
      isCurrent: w === currentIsoWeek && selectedYear.value === currentYear,
    });
  }
  return weeks;
});

const selectedWeekNumber = ref(getISOWeek(new Date()));

// Custom date inputs (YYYY-MM-DD)
const defaultStart = startOfWeek(new Date(), { weekStartsOn: 1 });
const defaultEnd = endOfWeek(new Date(), { weekStartsOn: 1 });

const customFromDate = ref(format(defaultStart, 'yyyy-MM-dd'));
const customToDate = ref(format(defaultEnd, 'yyyy-MM-dd'));
const validationError = ref('');

// Computed active period summary
const activePeriodSummary = computed(() => {
  if (filterMode.value === 'weekly') {
    const active = availableWeeks.value.find(w => w.weekNumber === selectedWeekNumber.value);
    return active ? active.label : '';
  }
  return `${customFromDate.value} to ${customToDate.value}`;
});

const validateDates = () => {
  validationError.value = '';
  if (!customFromDate.value || !customToDate.value) {
    validationError.value = t('REPORT.KPI_FILTERS.ERR_DATES_REQUIRED');
    return false;
  }
  const from = parseISO(customFromDate.value);
  const to = parseISO(customToDate.value);
  if (isAfter(from, to)) {
    validationError.value = t('REPORT.KPI_FILTERS.ERR_INVALID_RANGE');
    return false;
  }
  return true;
};

const applyWeeklyFilter = () => {
  const active = availableWeeks.value.find(w => w.weekNumber === selectedWeekNumber.value);
  if (!active) return;

  emit('filterChange', {
    from: active.startTimestamp,
    to: active.endTimestamp,
    mode: 'weekly',
    weekNumber: active.weekNumber,
    year: active.year,
    label: active.label,
  });
};

const applyCustomFilter = () => {
  if (!validateDates()) return;

  const start = new Date(`${customFromDate.value}T00:00:00`);
  const end = new Date(`${customToDate.value}T23:59:59`);

  emit('filterChange', {
    from: Math.floor(start.getTime() / 1000),
    to: Math.floor(end.getTime() / 1000),
    mode: 'custom',
    label: `${customFromDate.value} to ${customToDate.value}`,
  });
};

const applyFilter = () => {
  if (filterMode.value === 'weekly') {
    applyWeeklyFilter();
  } else {
    applyCustomFilter();
  }
};

const onSelectWeek = () => {
  const active = availableWeeks.value.find(w => w.weekNumber === selectedWeekNumber.value);
  if (active) {
    customFromDate.value = active.startDate;
    customToDate.value = active.endDate;
    applyWeeklyFilter();
  }
};

// Quick selections
const setThisWeek = () => {
  filterMode.value = 'weekly';
  selectedYear.value = currentYear;
  selectedWeekNumber.value = getISOWeek(new Date());
  onSelectWeek();
};

const setLastWeek = () => {
  const lastWeekDate = subWeeks(new Date(), 1);
  filterMode.value = 'weekly';
  selectedYear.value = getYear(lastWeekDate);
  selectedWeekNumber.value = getISOWeek(lastWeekDate);
  onSelectWeek();
};

const setThisMonth = () => {
  filterMode.value = 'custom';
  const start = startOfMonth(new Date());
  const end = endOfMonth(new Date());
  customFromDate.value = format(start, 'yyyy-MM-dd');
  customToDate.value = format(end, 'yyyy-MM-dd');
  applyCustomFilter();
};

const setLastMonth = () => {
  filterMode.value = 'custom';
  const lastMonthDate = subMonths(new Date(), 1);
  const start = startOfMonth(lastMonthDate);
  const end = endOfMonth(lastMonthDate);
  customFromDate.value = format(start, 'yyyy-MM-dd');
  customToDate.value = format(end, 'yyyy-MM-dd');
  applyCustomFilter();
};

const resetFilter = () => {
  setThisWeek();
  emit('resetFilter');
};

onMounted(() => {
  // If initial timestamps provided, parse them
  if (props.initialFrom && props.initialTo) {
    const fromDate = new Date(props.initialFrom * 1000);
    const toDate = new Date(props.initialTo * 1000);
    customFromDate.value = format(fromDate, 'yyyy-MM-dd');
    customToDate.value = format(toDate, 'yyyy-MM-dd');
    selectedWeekNumber.value = getISOWeek(fromDate);
    selectedYear.value = getYear(fromDate);
  }
  applyFilter();
});
</script>

<template>
  <div class="p-4 bg-n-surface-1 border border-n-strong rounded-xl shadow-sm mb-6 flex flex-col gap-4">
    <!-- Top Filter Header: Mode Selector & Quick Actions -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-n-slate-4 pb-3">
      <!-- Mode Tabs -->
      <div class="inline-flex p-1 bg-n-slate-3 rounded-lg">
        <button
          type="button"
          :class="[
            'px-3 py-1.5 text-xs font-semibold rounded-md transition-colors',
            filterMode === 'weekly'
              ? 'bg-n-surface-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12',
          ]"
          @click="filterMode = 'weekly'"
        >
          <span class="flex items-center gap-1.5">
            <Icon icon="i-lucide-calendar-range" class="w-3.5 h-3.5" />
            {{ $t('REPORT.KPI_FILTERS.WEEKLY_SELECTION') }}
          </span>
        </button>
        <button
          type="button"
          :class="[
            'px-3 py-1.5 text-xs font-semibold rounded-md transition-colors',
            filterMode === 'custom'
              ? 'bg-n-surface-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12',
          ]"
          @click="filterMode = 'custom'"
        >
          <span class="flex items-center gap-1.5">
            <Icon icon="i-lucide-calendar" class="w-3.5 h-3.5" />
            {{ $t('REPORT.KPI_FILTERS.CUSTOM_RANGE') }}
          </span>
        </button>
      </div>

      <!-- Quick Preset Buttons -->
      <div class="flex flex-wrap items-center gap-1.5">
        <span class="text-xs text-n-slate-10 mr-1">{{ $t('REPORT.KPI_FILTERS.QUICK_SELECT') }}:</span>
        <button
          type="button"
          class="px-2 py-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-slate-3 hover:bg-n-slate-4 rounded-md transition-colors"
          @click="setThisWeek"
        >
          {{ $t('REPORT.KPI_FILTERS.THIS_WEEK') }}
        </button>
        <button
          type="button"
          class="px-2 py-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-slate-3 hover:bg-n-slate-4 rounded-md transition-colors"
          @click="setLastWeek"
        >
          {{ $t('REPORT.KPI_FILTERS.LAST_WEEK') }}
        </button>
        <button
          type="button"
          class="px-2 py-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-slate-3 hover:bg-n-slate-4 rounded-md transition-colors"
          @click="setThisMonth"
        >
          {{ $t('REPORT.KPI_FILTERS.THIS_MONTH') }}
        </button>
        <button
          type="button"
          class="px-2 py-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-slate-3 hover:bg-n-slate-4 rounded-md transition-colors"
          @click="setLastMonth"
        >
          {{ $t('REPORT.KPI_FILTERS.LAST_MONTH') }}
        </button>
      </div>
    </div>

    <!-- Active Input Controls -->
    <div class="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
      <!-- Option 1: Weekly Selection Controls -->
      <div v-if="filterMode === 'weekly'" class="flex flex-wrap items-center gap-3">
        <div class="flex items-center gap-2">
          <label class="text-xs font-medium text-n-slate-11">{{ $t('REPORT.KPI_FILTERS.YEAR') }}:</label>
          <select
            v-model="selectedYear"
            class="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-n-strong bg-n-surface-1 text-n-slate-12 focus:ring-1 focus:ring-n-brand"
            @change="onSelectWeek"
          >
            <option v-for="yr in yearOptions" :key="yr" :value="yr">
              {{ yr }}
            </option>
          </select>
        </div>

        <div class="flex items-center gap-2">
          <label class="text-xs font-medium text-n-slate-11">{{ $t('REPORT.KPI_FILTERS.WEEK') }}:</label>
          <select
            v-model="selectedWeekNumber"
            class="min-w-[280px] px-3 py-1.5 text-xs font-medium rounded-lg border border-n-strong bg-n-surface-1 text-n-slate-12 focus:ring-1 focus:ring-n-brand"
            @change="onSelectWeek"
          >
            <option
              v-for="week in availableWeeks"
              :key="`${week.year}-${week.weekNumber}`"
              :value="week.weekNumber"
            >
              {{ week.label }} {{ week.isCurrent ? '(Current)' : '' }}
            </option>
          </select>
        </div>
      </div>

      <!-- Option 2: Custom Date Range Controls -->
      <div v-else class="flex flex-wrap items-center gap-3">
        <div class="flex items-center gap-2">
          <label class="text-xs font-medium text-n-slate-11">{{ $t('REPORT.KPI_FILTERS.FROM_DATE') }}:</label>
          <input
            v-model="customFromDate"
            type="date"
            class="px-3 py-1.5 text-xs font-medium rounded-lg border border-n-strong bg-n-surface-1 text-n-slate-12 focus:ring-1 focus:ring-n-brand"
          />
        </div>

        <div class="flex items-center gap-2">
          <label class="text-xs font-medium text-n-slate-11">{{ $t('REPORT.KPI_FILTERS.TO_DATE') }}:</label>
          <input
            v-model="customToDate"
            type="date"
            class="px-3 py-1.5 text-xs font-medium rounded-lg border border-n-strong bg-n-surface-1 text-n-slate-12 focus:ring-1 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- Actions: Apply & Reset -->
      <div class="flex items-center gap-2 self-end md:self-auto">
        <Button
          :label="$t('REPORT.KPI_FILTERS.APPLY')"
          size="sm"
          color="blue"
          :is-loading="isLoading"
          icon="i-lucide-check"
          @click="applyFilter"
        />
        <Button
          :label="$t('REPORT.KPI_FILTERS.RESET')"
          size="sm"
          variant="outline"
          color="slate"
          icon="i-lucide-rotate-ccw"
          @click="resetFilter"
        />
      </div>
    </div>

    <!-- Validation Error Alert -->
    <div v-if="validationError" class="p-2.5 text-xs text-n-ruby-11 bg-n-ruby-3 border border-n-ruby-6 rounded-lg flex items-center gap-2">
      <Icon icon="i-lucide-alert-circle" class="w-4 h-4 flex-shrink-0" />
      <span>{{ validationError }}</span>
    </div>

    <!-- Active Filter Indicator Banner -->
    <div class="flex items-center justify-between text-xs text-n-slate-11 bg-n-slate-2 px-3 py-1.5 rounded-lg border border-n-slate-4">
      <div class="flex items-center gap-2">
        <span class="font-semibold text-n-slate-12">{{ $t('REPORT.KPI_FILTERS.ACTIVE_PERIOD') }}:</span>
        <span class="font-medium text-n-brand">{{ activePeriodSummary }}</span>
      </div>
      <span class="text-[11px] text-n-slate-10">{{ $t('REPORT.KPI_FILTERS.TIMEZONE_APPLIED') }}</span>
    </div>
  </div>
</template>
